import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useEvents } from '../contexts/EventContext';

/**
 * Customer-side command palette (⌘K / Ctrl-K) for the organization workspace.
 *
 * Green-accented sibling of the operator palette. Blends:
 *   • Actions   — create event, manage guests, seating, venues
 *   • Navigate  — the org's workspace pages
 *   • Events    — this organization's events (select → work on it)
 *
 * Fully keyboard-driven: ↑/↓, Enter, Esc.
 */

type Group = 'Actions' | 'Navigate' | 'Events';

interface Command {
  id: string;
  group: Group;
  title: string;
  subtitle?: string;
  icon: string;
  keywords?: string;
  run: () => void;
}

interface EventRow { id: string; name: string; date?: string | null; status?: string; venue?: { name: string } | null }

function score(query: string, text: string): number {
  if (!query) return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  const idx = t.indexOf(q);
  if (idx >= 0) return 1000 - idx;
  let qi = 0, s = 0, last = -1;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) { s += last === i - 1 ? 3 : 1; last = i; qi++; }
  }
  return qi === q.length ? s : -1;
}

export function CustomerCommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { events: ctxEvents, selectEvent } = useEvents();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [events, setEvents] = useState<EventRow[]>([]);
  // Tenant-level pillar modules (e.g. 'giving') — gate pillar commands the same
  // way the sidebar nav and intent launcher do.
  const [tenantModules, setTenantModules] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Seed from context events immediately; refresh from /events on open.
  useEffect(() => {
    if (ctxEvents?.length) setEvents(ctxEvents as any);
  }, [ctxEvents]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiClient<EventRow[]>('/events').then((e) => { if (!cancelled) setEvents(e || []); }).catch(() => {});
    apiClient<{ tenantModules?: string[] }>('/auth/me')
      .then((r) => { if (!cancelled) setTenantModules(r?.tenantModules ?? []); })
      .catch(() => {});
    setQuery(''); setActive(0);
    setTimeout(() => inputRef.current?.focus(), 20);
    return () => { cancelled = true; };
  }, [open]);

  const go = (path: string) => { onClose(); navigate(path); };
  const workOn = (id: string) => { onClose(); selectEvent(id); navigate('/dashboard'); };

  const commands = useMemo<Command[]>(() => {
    const actions: Command[] = [
      { id: 'act-create-event', group: 'Actions', title: 'Create an event', subtitle: 'New event in your workspace', icon: '➕', keywords: 'new add', run: () => go('/events') },
      { id: 'act-guests', group: 'Actions', title: 'Manage guests', subtitle: 'Registrations & imports', icon: '🎟️', keywords: 'registrations rsvp attendees', run: () => go('/registrations') },
      { id: 'act-seating', group: 'Actions', title: 'Seating & tables', subtitle: 'Floorplan & engine', icon: '🪑', keywords: 'tables floorplan seats', run: () => go('/manage-event') },
      { id: 'act-venues', group: 'Actions', title: 'Venues', subtitle: 'Where events happen', icon: '📍', keywords: 'location', run: () => go('/venues') },
    ];
    const nav: Command[] = [
      { id: 'nav-dashboard', group: 'Navigate', title: 'Dashboard', icon: '🏠', keywords: 'home overview', run: () => go('/dashboard') },
      { id: 'nav-events', group: 'Navigate', title: 'Events', icon: '📅', keywords: '', run: () => go('/events') },
      { id: 'nav-venues', group: 'Navigate', title: 'Venues', icon: '📍', keywords: '', run: () => go('/venues') },
      { id: 'nav-modules', group: 'Navigate', title: 'Event modules', icon: '🧩', keywords: 'features toggles', run: () => go('/event-modules') },
      { id: 'nav-announcements', group: 'Navigate', title: 'Announcements', icon: '📢', keywords: 'comms broadcast', run: () => go('/announcements') },
    ];
    // Giving pillar commands — only when the tenant has 'giving' enabled.
    if (tenantModules.includes('giving')) {
      actions.push({ id: 'act-create-campaign', group: 'Actions', title: 'Create Giving Campaign', subtitle: 'Start a fundraising campaign', icon: '💚', keywords: 'fundraising donate donor giving campaign', run: () => go('/giving?new=1') });
      nav.push({ id: 'nav-giving', group: 'Navigate', title: 'Campaigns', icon: '💚', keywords: 'giving fundraising donors', run: () => go('/giving') });
    }
    const eventCmds: Command[] = events.map((e) => ({
      id: `evt-${e.id}`, group: 'Events', title: e.name,
      subtitle: [e.venue?.name, e.status].filter(Boolean).join(' · ') || undefined,
      icon: '📅', keywords: e.venue?.name || '', run: () => workOn(e.id),
    }));
    return [...actions, ...nav, ...eventCmds];
  }, [events, tenantModules]);

  const filtered = useMemo(() => {
    if (!query.trim()) return commands.filter((c) => c.group === 'Actions' || c.group === 'Navigate');
    return commands
      .map((c) => ({ c, s: Math.max(score(query, c.title), score(query, c.keywords || '') - 2, score(query, c.subtitle || '') - 4) }))
      .filter((x) => x.s > -1)
      .sort((a, b) => b.s - a.s)
      .slice(0, 40)
      .map((x) => x.c);
  }, [query, commands]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); filtered[active]?.run(); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  const groupsOrder: Group[] = ['Actions', 'Navigate', 'Events'];
  let runningIdx = -1;

  return (
    <div style={styles.scrim} onMouseDown={onClose}>
      <div style={styles.palette} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Command palette">
        <div style={styles.inputRow}>
          <span style={styles.searchIcon}>⌘</span>
          <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Search events — or type a command…" style={styles.input} aria-label="Command search" />
          <kbd style={styles.escHint}>esc</kbd>
        </div>
        <div style={styles.results} ref={listRef}>
          {filtered.length === 0 ? (
            <div style={styles.empty}>No matches for “{query}”.</div>
          ) : (
            groupsOrder.map((g) => {
              const items = filtered.filter((c) => c.group === g);
              if (items.length === 0) return null;
              return (
                <div key={g}>
                  <div style={styles.groupLabel}>{g}</div>
                  {items.map((c) => {
                    runningIdx += 1;
                    const idx = runningIdx;
                    const isActive = idx === active;
                    return (
                      <div key={c.id} data-idx={idx} onMouseEnter={() => setActive(idx)} onClick={() => c.run()}
                        style={{ ...styles.item, ...(isActive ? styles.itemActive : null) }}>
                        <span style={{ ...styles.itemIcon, ...(isActive ? styles.itemIconActive : null) }}>{c.icon}</span>
                        <span style={styles.itemBody}>
                          <span style={styles.itemTitle}>{c.title}</span>
                          {c.subtitle && <span style={styles.itemSub}>{c.subtitle}</span>}
                        </span>
                        {isActive && <span style={styles.enterHint}>↵</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
        <div style={styles.footer}>
          <span><kbd style={styles.kbd}>↑</kbd><kbd style={styles.kbd}>↓</kbd> navigate</span>
          <span><kbd style={styles.kbd}>↵</kbd> select</span>
          <span><kbd style={styles.kbd}>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

const ACCENT = '#1a6b3c';
const styles: Record<string, React.CSSProperties> = {
  scrim: { position: 'fixed', inset: 0, background: 'rgba(10,20,14,0.5)', backdropFilter: 'blur(3px)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh' },
  palette: { width: 'min(620px, 94vw)', background: '#fff', borderRadius: 16, boxShadow: '0 30px 80px -20px rgba(10,20,14,0.55)', overflow: 'hidden', border: '1px solid #e5e7eb' },
  inputRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px', borderBottom: '1px solid #eef0f4' },
  searchIcon: { fontSize: '1rem', color: ACCENT, fontWeight: 800 },
  input: { flex: 1, border: 'none', outline: 'none', fontSize: '1.05rem', color: '#0f2d1f', background: 'transparent' },
  escHint: { fontSize: '0.68rem', color: '#9aa4b2', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 6px' },
  results: { maxHeight: '52vh', overflowY: 'auto', padding: '8px 8px 12px' },
  groupLabel: { fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9aa4b2', padding: '10px 12px 4px' },
  item: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, cursor: 'pointer' },
  itemActive: { background: 'linear-gradient(90deg, #ecfdf5, #f0fdf4)' },
  itemIcon: { width: 32, height: 32, borderRadius: 9, display: 'grid', placeItems: 'center', background: '#f1f5f4', fontSize: '1rem', flexShrink: 0 },
  itemIconActive: { background: '#fff', boxShadow: `0 2px 8px -2px ${ACCENT}66` },
  itemBody: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
  itemTitle: { fontSize: '0.92rem', fontWeight: 700, color: '#0f2d1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  itemSub: { fontSize: '0.76rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  enterHint: { color: ACCENT, fontWeight: 800, fontSize: '0.9rem' },
  empty: { padding: '28px 16px', textAlign: 'center', color: '#9aa4b2', fontSize: '0.9rem' },
  footer: { display: 'flex', gap: 16, alignItems: 'center', padding: '10px 16px', borderTop: '1px solid #eef0f4', fontSize: '0.72rem', color: '#9aa4b2' },
  kbd: { border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 5px', margin: '0 2px', fontSize: '0.7rem', color: '#6b7280' },
};
